/**
 * Whether a rejected video.play() means autoplay was blocked and the hero
 * should show its still image. AbortError is a load race or an interrupted
 * play() and should be retried rather than treated as Low Power Mode.
 * @param {Error|DOMException|null|undefined} error
 * @return {boolean}
 */
function playbackRejectionShouldFallback(error) {
  if (!error) {
    return false;
  }
  return error.name !== 'AbortError';
}

/**
 * Whether script should call play(). An already-playing video is left to the
 * browser, which pauses it once it leaves the viewport. Calling play() on it
 * disables that pause.
 * @param {Object|null} state
 * @param {boolean} [state.paused] - video is paused
 * @param {boolean} [state.visible] - video is visible enough to play
 * @param {boolean} [state.fallbackShown] - still is already showing
 * @return {boolean}
 */
function shouldAttemptPlayback(state) {
  if (!state || state.fallbackShown || !state.visible || !state.paused) {
    return false;
  }
  return true;
}

/**
 * Whether script should pause a video it started, because the video has
 * left the viewport. Browser-managed autoplay is not paused here.
 * A play() that has not resolved yet still reports the video as paused,
 * so an in-flight play must be paused too or it will start off-screen.
 * @param {Object|null} state
 * @param {boolean} [state.startedByScript] - this script called play()
 * @param {boolean} [state.visible] - any of the video is on screen
 * @param {boolean} [state.paused] - video is paused
 * @param {boolean} [state.playPending] - play() has not settled
 * @param {boolean} [state.fallbackShown] - still is already showing
 * @return {boolean}
 */
function shouldPauseForVisibility(state) {
  if (!state || state.fallbackShown || !state.startedByScript || state.visible) {
    return false;
  }
  if (state.paused && !state.playPending) {
    return false;
  }
  return true;
}

/**
 * Whether an AbortError should use the one playback retry. Pausing for
 * scroll, or any interruption after the video has left the viewport, is
 * not a load race and must not consume that retry.
 * @param {Error|DOMException|null|undefined} error
 * @param {Object|null} state
 * @param {boolean} [state.visibilityPause] - script paused the video
 * @param {boolean} [state.onScreen] - video is still in the viewport
 * @return {boolean}
 */
function playbackAbortShouldRetry(error, state) {
  if (!error || error.name !== 'AbortError') {
    return false;
  }
  if (!state || state.visibilityPause || !state.onScreen) {
    return false;
  }
  return true;
}

module.exports = {
  playbackRejectionShouldFallback,
  shouldAttemptPlayback,
  shouldPauseForVisibility,
  playbackAbortShouldRetry,
};
